import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import _ from 'lodash';
import { Subscription } from 'rxjs';
import { OrderableElementsModel, YellowBrickRoadModel } from '../../../jobs/applicant-service/models/yellow-brick-road-model';
import { YellowBrickRoadService } from './yellow-brick-road.service';

@Component({
  selector: 'app-yellow-brick-road',
  templateUrl: './yellow-brick-road.component.html',
  styleUrls: ['./yellow-brick-road.component.scss']
})
export class YellowBrickRoadComponent implements OnInit, OnDestroy {
  private baseJobUrl: string;
  private subscription: Subscription = new Subscription();

  applicantId: string;
  identifier: string;
  jobId: number;
  model: YellowBrickRoadModel;
  isFirstIterativeApplicationPart: boolean;
  navigableIterativeApplicationElements: Array<OrderableElementsModel>;

  constructor(private route: ActivatedRoute, private router: Router, private ybrService: YellowBrickRoadService) {
    this.subscription.add(
      ybrService.modelUpdated$.subscribe(
        (ybr: YellowBrickRoadModel) => {
          this.navigableIterativeApplicationElements = ybr.orderableElements
            .filter((x: OrderableElementsModel) => x.type !== 'Resume' && x.type !== 'CoverLetter');
          this.model = ybr;
        })
    );

    this.subscription.add(
      ybrService.isPartOneUpdated$.subscribe(
        (isFirstIterativeApplicationPart: boolean) => {
          if (isFirstIterativeApplicationPart === undefined) {
            this.isFirstIterativeApplicationPart = true;
          } else {
            this.isFirstIterativeApplicationPart = isFirstIterativeApplicationPart;
          }
        })
    );
   }

  canNavigateToEducationHistory(): boolean {
    return this.model.educationHistory.percentageComplete === 100;
  }

  canNavigateToEmploymentHistory(): boolean {
    return this.model.employmentHistory.percentageComplete === 100;
  }

  canNavigateToProfile(): boolean {
    return this.model.profilePercentageComplete === 100;
  }

  canNavigateToElement(element: OrderableElementsModel): boolean {
    if (this.model.personalInformationPercentageComplete < 100) { return false; }

    const elementIndex = this.model.orderableElements.findIndex(x => x.id === element.id);

    if (elementIndex === 0) {
      return true;
    }

    for (let i = elementIndex - 1; i >= 0; i--) {
      if (this.model.orderableElements[i].type === 'Custom' || this.model.orderableElements[i].type === 'AutoScore') {
        const customElement = this.model.surveyElements.find(x => x.id === this.model.orderableElements[i].id);
        if (customElement.percentageComplete < 100) {
           return false;
        }
      } else if (this.model.orderableElements[i].type === 'EducationHistory') {
        if (this.model.educationHistory.percentageComplete < 100) {
          return false;
        }
      } else if (this.model.orderableElements[i].type === 'EmploymentHistory') {
        if (this.model.employmentHistory.percentageComplete < 100) {
          return false;
        }
      } else if (this.model.orderableElements[i].type === 'References') {
        if (this.model.references.percentageComplete < 100) {
          return false;
        }
      }

      return true;
    }
  }

  canNavigateToVoluntaryIdentification(): boolean {
    return this.model.eeocForm.percentageComplete === 100;
  }

  canNavigateToReviewAndSubmit(): boolean {
    for (let i = 0; i < this.navigableIterativeApplicationElements.length; i++) {
      if (!this.canNavigateToElement(this.navigableIterativeApplicationElements[0])) {
        return false;
      }
    }
    
    return this.canNavigateToProfile();
  }

  getProgressClass(percentageComplete: number): string {
    if (percentageComplete === 100) { return 'full'; }
    if (percentageComplete > 0 && percentageComplete < 100) { return 'half'; }

    return '';
  }

  getProgressClassElement(element: OrderableElementsModel): string {
    let percentageComplete = 0;

    if (element.type === 'Custom' || element.type === 'AutoScore') {
      const customElement = this.model.surveyElements.find(x => x.id === element.id);
      percentageComplete = customElement.percentageComplete;
    } else if (element.type === 'EducationHistory') {
      percentageComplete = this.model.educationHistory.percentageComplete;
    } else if (element.type === 'EmploymentHistory') {
      percentageComplete = this.model.employmentHistory.percentageComplete;
    } else if (element.type === 'References') {
      percentageComplete = this.model.references.percentageComplete;
    }

    return this.getProgressClass(percentageComplete);
  }

  getRouterLink(urlPart: string): string {
    return `${this.baseJobUrl}/${urlPart}`;
  }

  getElementRouterLink(element: OrderableElementsModel): string {
    if (element.type === 'Custom' || element.type === 'AutoScore') {
      const urlPart = 'survey/' + element.id;
      const elementId = _.last(this.router.url.split('/'));
      if (+elementId === 0 && +_.last(urlPart.split('/')) === this.model.surveyElements[0].id) {
        return `${this.baseJobUrl}/survey/0`;
      }
      return `${this.baseJobUrl}/${urlPart}`;
    } else if (element.type === 'EducationHistory') {
      return `${this.baseJobUrl}/education-history`;
    } else if (element.type === 'EmploymentHistory') {
      return `${this.baseJobUrl}/employment-history`;
    } else if (element.type === 'References') {
      return `${this.baseJobUrl}/references`;
    } else if (element.type === 'EEOForm' || element.type === 'OFCCPForm') {
      return `${this.baseJobUrl}/voluntary-identification`;
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  ngOnInit() {
    this.subscription.add(this.route.params.subscribe((params: Params) => {
      this.identifier = params['identifier'];
      this.jobId = params['jobId'];
      this.applicantId = params['applicantId'];
      this.baseJobUrl = `/${this.identifier}/jobs/${this.jobId}/apply/${this.applicantId}`;
    }));
    
    this.subscription.add(this.router.events
      .subscribe((val) => {
        this.applyYellowBrickRoadChanges();
        }
      )
    );

    this.applyYellowBrickRoadChanges();
  }

  applyYellowBrickRoadChanges() {
    this.subscription.add(this.route.children[0].data
      .subscribe((data: { model: { yellowBrickRoad: YellowBrickRoadModel, isFirstIterativeApplicationPart: boolean } }) => {
        this.ybrService.updateIsPartOne(data.model.isFirstIterativeApplicationPart);
        this.ybrService.updateModel(data.model.yellowBrickRoad);
      })
    );
  }
}
